import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { Printer, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const CATS = ["PTS", "REB", "AST", "STL", "BLK", "FLS"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4", "OT", "FINAL"];

function Marker({ className }) {
    return <div className={`absolute w-5 h-5 bg-black ${className}`} aria-hidden="true" />;
}

function TeamSheet({ game, side, data }) {
    const opponent = side === "home" ? game.away?.team : game.home?.team;
    const code = `GM-${game.id.slice(0, 6).toUpperCase()}-${side === "home" ? "H" : "A"}`;
    const padded = Math.max(0, 12 - (data?.players?.length || 0));
    return (
        <div
            data-testid={`sheet-${side}-${game.id}`}
            className="sheet-page relative bg-white text-black w-full max-w-[1080px] mx-auto p-4 sm:p-5 shadow-2xl"
        >
            <Marker className="top-3 left-3" />
            <Marker className="top-3 right-3" />
            <Marker className="bottom-3 left-3" />
            <Marker className="bottom-3 right-3" />

            <div className="flex items-start justify-between border-b-4 border-black pb-2.5 gap-4">
                <div>
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase">YKPBL — Official Game Stat Sheet</p>
                    <h1 className="text-2xl font-black uppercase leading-tight mt-1">{data?.team?.name}</h1>
                    <p className="text-xs font-bold uppercase tracking-widest mt-1">
                        {side} team <span className="mx-1">vs</span> {opponent?.name}
                    </p>
                </div>
                <div className="text-right font-mono text-xs shrink-0">
                    <p data-testid={`sheet-code-${side}-${game.id}`} className="text-base font-black tracking-wider">{code}</p>
                    <p>{format(new Date(game.date), "MMM d, yyyy • h:mm a")}</p>
                    {game.venue && <p>{game.venue}</p>}
                    {game.week != null && <p>Week {game.week}</p>}
                </div>
            </div>

            <table className="w-full mt-3 border-2 border-black border-collapse">
                <thead>
                    <tr className="bg-black text-white">
                        <th className="border border-black px-2 py-1.5 text-xs w-14">NO.</th>
                        <th className="border border-black px-2 py-1.5 text-xs text-left">PLAYER</th>
                        {CATS.map((c) => (
                            <th key={c} className="border border-black px-1 py-1.5 text-xs w-20">{c}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(data?.players || []).map((p) => (
                        <tr key={p.id} data-testid={`sheet-row-${p.id}`}>
                            <td className="border-2 border-black text-center font-black text-base h-9">{p.number}</td>
                            <td className="border-2 border-black px-2 font-bold uppercase text-sm">
                                {p.name}
                                <span className="ml-2 text-[10px] font-normal text-neutral-500">{p.position}</span>
                            </td>
                            {CATS.map((c) => (
                                <td key={c} className="border-2 border-black" />
                            ))}
                        </tr>
                    ))}
                    {Array.from({ length: padded }).map((_, i) => (
                        <tr key={`pad-${i}`}>
                            <td className="border-2 border-black h-9" />
                            <td className="border-2 border-black" />
                            {CATS.map((c) => (
                                <td key={c} className="border-2 border-black" />
                            ))}
                        </tr>
                    ))}
                    <tr className="bg-neutral-200">
                        <td colSpan={2} className="border-2 border-black px-2 py-1.5 text-xs font-black uppercase tracking-widest">
                            Team Totals
                        </td>
                        {CATS.map((c) => (
                            <td key={c} className="border-2 border-black" />
                        ))}
                    </tr>
                </tbody>
            </table>

            <div className="mt-3 flex items-start justify-between gap-6">
                <table className="border-2 border-black border-collapse">
                    <thead>
                        <tr className="bg-black text-white">
                            {QUARTERS.map((q) => (
                                <th key={q} className="border border-black px-3 py-1 text-[10px]">{q}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {QUARTERS.map((q) => (
                                <td key={q} className="border-2 border-black w-16 h-8" />
                            ))}
                        </tr>
                    </tbody>
                </table>
                <div className="text-[10px] font-mono text-right space-y-2 pt-0.5">
                    <p>Scorekeeper: ______________________</p>
                    <p>Referee: ______________________</p>
                </div>
            </div>
            <p className="mt-2.5 text-[9px] font-mono text-neutral-500 uppercase tracking-widest text-center">
                Scan tip: photograph flat, all four corner markers visible, good light — the system reads this sheet automatically.
            </p>
        </div>
    );
}

function TickRow({ n, numbered, small }) {
    return (
        <div className="flex flex-nowrap items-center justify-center gap-[3px] px-0.5 py-1">
            {Array.from({ length: n }).map((_, i) => (
                <span
                    key={i}
                    className={`inline-flex items-center justify-center border border-neutral-500 rounded-[3px] font-mono text-neutral-400 ${
                        small ? "w-3 h-3 text-[7px]" : "w-3.5 h-3.5 text-[8px]"
                    }`}
                >
                    {numbered ? i + 1 : ""}
                </span>
            ))}
        </div>
    );
}

const HUSTLE = ["REB", "AST", "STL", "BLK"];

function TeamTally({ game, side, data }) {
    const opponent = side === "home" ? game.away?.team : game.home?.team;
    const code = `GM-${game.id.slice(0, 6).toUpperCase()}-${side === "home" ? "H" : "A"}-TALLY`;
    const padded = Math.max(0, 10 - (data?.players?.length || 0));
    const QS = ["Q1", "Q2", "Q3", "Q4", "OT"];
    const renderRow = (p, key) => (
        <tr key={key} data-testid={p ? `tally-row-${p.id}` : undefined}>
            <td className="border-2 border-black text-center font-black text-base h-8">{p?.number}</td>
            <td className="border-2 border-black px-3 font-bold uppercase text-xs">{p?.name}</td>
            {QS.slice(0, 4).map((q) => (
                <td key={q} className="border-2 border-black" />
            ))}
            {HUSTLE.map((h) => (
                <td key={h} className="border-2 border-black" />
            ))}
        </tr>
    );
    return (
        <div
            data-testid={`tally-${side}-${game.id}`}
            className="sheet-page relative bg-white text-black w-full max-w-[1080px] mx-auto p-4 shadow-2xl"
        >
            <Marker className="top-3 left-3" />
            <Marker className="top-3 right-3" />
            <Marker className="bottom-3 left-3" />
            <Marker className="bottom-3 right-3" />

            <div className="text-center border-b-4 border-black pb-2">
                <p className="text-[10px] font-bold tracking-[0.35em] uppercase">YKP Basketball League</p>
                <h1 className="text-2xl font-black uppercase leading-tight mt-0.5">Tally Sheet</h1>
                <p className="text-xs font-bold uppercase tracking-widest mt-1">
                    {data?.team?.name} <span className="mx-1 text-neutral-400">vs</span> {opponent?.name}
                </p>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-1.5 border-2 border-black px-4 py-1 font-mono text-[11px]">
                <p><span className="font-black">GAME:</span> <span data-testid={`tally-code-${side}-${game.id}`}>{code}</span></p>
                <p><span className="font-black">DATE:</span> {format(new Date(game.date), "MMM d, yyyy • h:mm a")}</p>
                {game.venue && <p><span className="font-black">VENUE:</span> {game.venue}</p>}
                <div className="flex items-center gap-2.5">
                    <span className="font-black">QTR:</span>
                    {["1Q", "2Q", "3Q", "4Q", "OT"].map((q) => (
                        <span key={q} className="inline-flex items-center gap-1">
                            <span className="inline-block w-3.5 h-3.5 border-2 border-black" />
                            {q}
                        </span>
                    ))}
                </div>
            </div>

            <p className="mt-1 text-center text-[9px] font-mono uppercase tracking-widest text-neutral-600">
                Write each player's points per quarter (Q1-Q4). Tally rebounds, assists, steals and blocks as numbers. Circle team fouls per quarter below.
            </p>

            <table className="w-full mt-2 border-2 border-black border-collapse" data-testid={`tally-table-${side}-${game.id}`}>
                <thead>
                    <tr className="bg-black text-white">
                        <th className="border border-black px-1 py-1.5 text-xs w-12">NO.</th>
                        <th className="border border-black px-3 py-1.5 text-xs text-left">NAME</th>
                        {QS.slice(0, 4).map((q) => (
                            <th key={q} className="border border-black px-1 py-1.5 text-[10px] w-[112px]">{q}</th>
                        ))}
                        {HUSTLE.map((h) => (
                            <th key={h} className="border border-black px-1 py-1.5 text-[10px] w-[84px]">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(data?.players || []).map((p) => renderRow(p, p.id))}
                    {Array.from({ length: padded }).map((_, i) => renderRow(null, `pad-${i}`))}
                </tbody>
            </table>

            <div className="mt-2.5 flex items-stretch gap-4">
                <div className="w-[46%] shrink-0 flex flex-col">
                    <table className="border-2 border-black border-collapse w-full">
                    <thead>
                        <tr className="bg-black text-white">
                            <th className="border border-black px-2 py-1 text-[10px] text-left w-20" />
                            {QS.map((q) => (
                                <th key={q} className="border border-black px-1 py-1 text-[10px]">{q}</th>
                            ))}
                            <th className="border border-black px-1 py-1 text-[10px] w-14">FINAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="border-2 border-black px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">Team Fouls</td>
                            {QS.map((q) => (
                                <td key={q} className="border-2 border-black"><TickRow n={5} numbered small /></td>
                            ))}
                            <td className="border-2 border-black bg-neutral-100" />
                        </tr>
                        <tr>
                            <td className="border-2 border-black px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">Score</td>
                            {QS.map((q) => (
                                <td key={q} className="border-2 border-black h-8" />
                            ))}
                            <td className="border-2 border-black h-8 bg-neutral-100" />
                        </tr>
                    </tbody>
                    </table>
                    <div
                        className="mt-1.5 flex items-center gap-4 border-2 border-black px-2 py-1.5"
                        data-testid={`tally-timeouts-${side}-${game.id}`}
                    >
                        <p className="text-[10px] font-black uppercase tracking-wider shrink-0">Time Outs</p>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-mono uppercase text-neutral-500">1st half</span>
                            {[0, 1].map((i) => (
                                <span key={i} className="inline-block w-4 h-4 border-2 border-black rounded-[3px]" />
                            ))}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-mono uppercase text-neutral-500">2nd half</span>
                            {[0, 1, 2].map((i) => (
                                <span key={i} className="inline-block w-4 h-4 border-2 border-black rounded-[3px]" />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex-1 border-2 border-black p-2.5">
                    <p className="text-[10px] font-black uppercase tracking-widest mb-1.5">
                        Running score — cross off the team total after every basket
                    </p>
                    <div className="grid grid-cols-[repeat(25,minmax(0,1fr))] gap-[3px]">
                        {Array.from({ length: 100 }, (_, i) => (
                            <span
                                key={i}
                                className="h-5 border border-neutral-400 rounded-[2px] text-[7px] font-mono text-neutral-500 flex items-center justify-center"
                            >
                                {i + 1}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-2 flex items-end justify-between gap-6 font-mono text-[10px]">
                <p>OFFICIAL SCOREKEEPER: ______________________</p>
                <p>REFEREE SIGN: ______________________</p>
            </div>
            <p className="mt-1 text-[9px] font-mono text-neutral-500 uppercase tracking-widest text-center">
                Pairs with the official stat sheet {code.replace("-TALLY", "")} — transfer totals after the game.
            </p>
        </div>
    );
}

export default function ScoreSheet() {
    const { gameId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/games/${gameId}/scoresheet`)
            .then((r) => setData(r.data))
            .catch(() => setError(true));
    }, [gameId]);

    if (error)
        return (
            <div className="min-h-screen bg-[#08080C] text-slate-300 flex items-center justify-center" data-testid="scoresheet-error">
                Game not found.
            </div>
        );
    if (!data)
        return (
            <div className="min-h-screen bg-[#08080C] text-slate-300 flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Preparing stat sheets…
            </div>
        );

    return (
        <div className="min-h-screen bg-neutral-800 py-8 print:bg-white print:py-0 overflow-x-auto">
            <style>{`
                @media print {
                    .grain-overlay { display: none !important; }
                    .sheet-page { page-break-after: always; box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
                    @page { size: A4 landscape; margin: 7mm; }
                }
            `}</style>
            <div className="print:hidden max-w-[1080px] mx-auto flex items-center justify-between px-4 mb-6">
                <Link to="/admin" className="flex items-center gap-2 text-slate-300 hover:text-white text-xs font-display font-bold uppercase tracking-widest">
                    <ArrowLeft className="w-4 h-4" /> Admin
                </Link>
                <button
                    data-testid="print-sheets-button"
                    onClick={() => window.print()}
                    className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                    <Printer className="w-4 h-4" /> Print All Sheets
                </button>
            </div>
            <p className="print:hidden max-w-[1080px] mx-auto px-4 mb-6 text-[11px] text-slate-400 font-display uppercase tracking-widest">
                4 landscape pages per game — stat sheet + live tally sheet (scoring, fouls, hustle, running score) for each team
            </p>
            <div className="space-y-8 print:space-y-0" data-testid={`scoresheet-${gameId}`}>
                <TeamSheet game={data} side="home" data={data.home} />
                <TeamTally game={data} side="home" data={data.home} />
                <TeamSheet game={data} side="away" data={data.away} />
                <TeamTally game={data} side="away" data={data.away} />
            </div>
        </div>
    );
}
